// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddToCartButton from "@steiner/App/Components/AddToCartButton/AddToCartButton";
import QtyField from "@steiner/App/Components/Qty/QtyField";
import { CustomAppState, ReorderPadItemModel } from "@steiner/App/Store/Reducers";
import { DataTableCellProps } from "@steiner/MobiusOverrides/DataTable/DataTableCell";
import ProductAddToCartButton from "@steiner/Overrides/Components/ProductAddToCartButton";
import { ProductAddToListLinkStyles } from "@steiner/Overrides/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@steiner/Overrides/Components/ProductPrice";
import { getFormattedDate } from "@steiner/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    widgetId: string;
    product?: ProductModel;
    order: ReorderPadItemModel;
    labels: { [key: string]: string };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    productInfo: ownProps.product ? getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id) : undefined,
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

interface ReorderPadItemStyles {
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataTableDescriptionCell?: DataTableCellProps;
    addToCartCell?: DataTableCellBaseProps;
    actionsContainer?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
}

export const reorderPadItemStyles: ReorderPadItemStyles = {};

const styles = reorderPadItemStyles;
const ReorderPadItem: FC<Props> = ({ order, product, productInfo, widgetId, labels, updateProductInfo }) => {
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product!.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product!.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        if (order) {
            return (
                <DataTableRow {...styles.dataTableRow}>
                    <DataTableCell {...styles.dataTableDescriptionCell} {...{ header: labels.productDescription }}>
                        <Typography>{order.productDescription}</Typography>
                    </DataTableCell>
                    <DataTableCell {...styles.dataTableCells} {...{ header: labels.lastOrdered }}>
                        {order.lastOrderedDate ? getFormattedDate(new Date(order.lastOrderedDate)) : "--"}
                    </DataTableCell>
                    <DataTableCell {...styles.dataTableCells} {...{ header: labels.lastOrderQTY }}>
                        {order.lastOrderedQty}
                    </DataTableCell>
                    <DataTableCell {...styles.dataTableCells} {...{ header: labels.price }}>
                        <Typography>{translate("N/A")}</Typography>
                    </DataTableCell>
                    <DataTableCell {...styles.addToCartCell} {...{ header: labels.addToCart }}>
                        <StyledWrapper {...styles.actionsContainer} data-test-selector="product-reorder-pad-actions">
                            <QtyField value="1" disabled={true} editable={false} />
                            <AddToCartButton
                                disabled={true}
                                displayAsIcon={true}
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </StyledWrapper>
                    </DataTableCell>
                </DataTableRow>
            );
        }
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <DataTableRow {...styles.dataTableRow}>
                <DataTableCell {...styles.dataTableDescriptionCell} {...{ header: labels.productDescription }}>
                    <ProductDescription product={productContext} />
                </DataTableCell>
                <DataTableCell {...styles.dataTableCells} {...{ header: labels.lastOrdered }}>
                    {order.lastOrderedDate ? getFormattedDate(new Date(order.lastOrderedDate)) : "--"}
                </DataTableCell>
                <DataTableCell {...styles.dataTableCells} {...{ header: labels.lastOrderQTY }}>
                    {order.lastOrderedQty}
                </DataTableCell>
                <DataTableCell {...styles.dataTableCells} {...{ header: labels.price }}>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSalePriceLabel={false}
                        extendedStyles={styles.productPriceStyles}
                    />
                </DataTableCell>
                <DataTableCell {...styles.addToCartCell} {...{ header: labels.addToCart }}>
                    <StyledWrapper {...styles.actionsContainer} data-test-selector="product-reorder-pad-actions">
                        <ProductQuantityOrdered
                            extendedStyles={styles.productQuantityOrderedStyles}
                            id={`${widgetId}-qty`}
                        />
                        <ProductAddToCartButton
                            displayAsIcon={true}
                            extendedStyles={styles.productAddToCartButtonStyles}
                        />
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                            labelOverride={translate("Product Group")}
                        />
                    </StyledWrapper>
                </DataTableCell>
            </DataTableRow>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadItem);
